<script lang="ts" setup>
const { t: $t } = useI18n()

import type { CompanyVehicleRentalCreate, CompanyVehicleRentalCreateOrUpdate, CompanyVehicleRentalUpdate } from '~/server/trpc/routers/companyVehicleRental'

const { openCompanyVehicleRentalCreateOrEditPopup: { data: popupData, close: closePopup } } = useGlobalOpeners()
const id = computed(() => popupData.value?.id ?? null)

const { companyVehicleRental: companyVehicleRentalQuery, user: userQuery, companyVehicle: companyVehicleQuery } = useQuery()
const { data: companyVehicleRental, isFetching } = companyVehicleRentalQuery.getById(id)
const { data: users } = userQuery.getPossibleCompanyVehicleContactPersons()
const possibleDrivers = computed(() => users.value ? users.value.map(u => ({ label: u.name ?? u.email, value: u.email })) : [])

const vehicleToIncludeId = computed(() => companyVehicleRental.value?.companyVehicle?.id)

const { data: availableCompanyVehiclesData } = companyVehicleQuery.getAllAvailableForRental(vehicleToIncludeId)
const availableCompanyVehicles = computed(() => availableCompanyVehiclesData.value ?? [])

const payload = computed(() => {
  if (!id.value) {
    return {
      mode: 'create',
      data: {
        companyVehicleId: popupData.value?.companyVehicleId ?? '',
        rentalStartsAt: new Date(),
        expectedRentalEndsAt: addDaysDstSafe(new Date(), 3),
        conditionStartOfRental: 'Clean',
        tankFillingStartOfRental: 'Full',
        mileageStartOfRental: 0,
        driverRentalStartSignature: '',
        vehiclePhotosStartOfRental: [],
        keyLocationPhotosStartOfRental: [],
        isExternalRental: false,
      },
    } satisfies CompanyVehicleRentalCreate
  }

  if (isFetching.value || !companyVehicleRental.value) {
    return
  }

  return {
    mode: 'update',
    data: {
      id: companyVehicleRental.value.id,
      companyVehicleId: companyVehicleRental.value.companyVehicle.id,
      rentalStartsAt: companyVehicleRental.value.rentalStartsAt,
      expectedRentalEndsAt: companyVehicleRental.value.expectedRentalEndsAt,
      conditionStartOfRental: companyVehicleRental.value.conditionStartOfRental,
      tankFillingStartOfRental: companyVehicleRental.value.tankFillingStartOfRental,
      mileageStartOfRental: companyVehicleRental.value.mileageStartOfRental,
      driverRentalStartSignature: companyVehicleRental.value.driverRentalStartSignature ?? '',
      vehiclePhotosStartOfRental: companyVehicleRental.value.vehiclePhotosStartOfRental,
      keyLocationPhotosStartOfRental: companyVehicleRental.value.keyLocationPhotosStartOfRental,
      isExternalRental: companyVehicleRental.value.isExternalRental,
      externalDriverName: companyVehicleRental.value.externalDriverName ?? undefined,
      externalDriverPhone: companyVehicleRental.value.externalDriverPhone ?? undefined,
      internalDriverEmail: companyVehicleRental.value.internalDriver?.email ?? undefined,
      destination: companyVehicleRental.value.destination ?? undefined,
      additionalInformation: companyVehicleRental.value.additionalInformation ?? undefined,
    },
  } satisfies CompanyVehicleRentalUpdate
})

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const update = useMutation({
  mutationFn: $trpc.companyVehicleRental.update.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('query.error', { entity: $t('companyVehicleRental.name') }) }),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['companyVehicleRental'] })
    notification.success({ title: $t('query.success', { entity: $t('companyVehicleRental.name') }), duration: 4500 })
    closePopup()
  },
})

const create = useMutation({
  mutationFn: $trpc.companyVehicleRental.create.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('query.error', { entity: $t('companyVehicleRental.name') }) }),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['companyVehicleRental'] })
    notification.success({ title: $t('query.success', { entity: $t('companyVehicleRental.name') }), duration: 4500 })
    closePopup()
  },
})

function saveCreateOrEditForm(payload: CompanyVehicleRentalCreateOrUpdate) {
  if (payload.mode === 'create') {
    return create.mutateAsync(payload.data)
  }

  if (popupData.value?.mode === 'output') {
    return update.mutateAsync({ ...payload.data, carOutputCreatedAt: new Date() })
  }

  return update.mutateAsync(payload.data)
}
</script>

<template>
  <ThePopup
    v-if="payload"
    :show="Boolean(popupData)"
    :title="$t(`companyVehicleRental.${popupData?.mode ?? payload.mode}.popup.title`)"
    width="1050px"
    @close="closePopup"
  >
    <CompanyVehicleRentalCreateOrEditForm
      :payload
      :possible-drivers
      :available-company-vehicles
      :mode="popupData?.mode"
      :is-pending="create.isPending.value || update.isPending.value"
      @save="saveCreateOrEditForm"
    />
  </ThePopup>
</template>
